import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { trackOrderByToken } from '../services/orderTrackingService';
import { getTrackingView } from './liveTrackingStatus';
import {
  TrackingAddress,
  TrackingItems,
  TrackingProgress
} from '../components/LiveTrackingSections';
import DeliveredReviewForm from '../components/DeliveredReviewForm';

const REFRESH_MS = 30000;

function paymentStatusLabel(order) {
  if (['paid', 'verified'].includes(order.payment_status)) {
    return 'Paid';
  }

  if (
    order.payment_method === 'cash_on_delivery' &&
    ['unpaid', 'pending', '', null, undefined].includes(order.payment_status)
  ) {
    return 'Pending to be paid upon delivery';
  }

  const labels = {
    unpaid: 'Pending',
    pending: 'Pending',
    proof_submitted: 'Proof submitted — awaiting confirmation',
    under_review: 'Under review',
    rejected: 'Payment rejected',
    new_proof_requested: 'New proof requested',
    partially_refunded: 'Partially refunded',
    refunded: 'Refunded'
  };

  return labels[order.payment_status] || 'Payment status unavailable';
}

function LiveOrderPage() {
  const { token } = useParams();
  const [state, setState] = useState({ status: 'loading', order: null });
  const [checkedAt, setCheckedAt] = useState(null);

  const load = useCallback(async () => {
    try {
      const order = await trackOrderByToken(token);
      setState({ status: order ? 'ready' : 'missing', order });
      setCheckedAt(new Date());
    } catch {
      setState((current) =>
        current.order ? current : { status: 'error', order: null }
      );
    }
  }, [token]);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  const { status, order } = state;
  const view = order ? getTrackingView(order) : null;

  return (
    <main className="live-track-page">
      <section className="live-track-card">
        <span className="live-track-eyebrow">ZEVI Order Update</span>

        {status === 'loading' && <p className="live-track-note">Loading your order…</p>}

        {status === 'missing' && (
          <>
            <h1>Link not found</h1>
            <p className="live-track-note">
              This tracking link is not valid. Please check the link ZEVI sent
              you, or contact us for help.
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <h1>Tracking unavailable</h1>
            <p className="live-track-note">
              We could not load your order right now. Please try again shortly.
            </p>
            <button className="live-track-retry" onClick={load}>Try again</button>
          </>
        )}

        {status === 'ready' && order && view && (
          <>
            <h1 className={`live-track-headline ${view.tone}`}>{view.headline}</h1>
            <p className="live-track-note">{view.message}</p>

            <dl className="live-track-facts">
              <div>
                <dt>Name</dt>
                <dd>{order.customer_name || 'ZEVI customer'}</dd>
              </div>
              <div>
                <dt>Order ID</dt>
                <dd>{order.order_number}</dd>
              </div>
              <div>
                <dt>Payment status</dt>
                <dd>{paymentStatusLabel(order)}</dd>
              </div>
              <div>
                <dt>Tracking number</dt>
                <dd>{order.tracking_number || 'Not assigned yet'}</dd>
              </div>
              {order.delivered && order.delivered_at && (
                <div>
                  <dt>Delivered on</dt>
                  <dd>{new Date(order.delivered_at).toLocaleDateString()}</dd>
                </div>
              )}
            </dl>

            {!order.closed && (
              <>
                <TrackingProgress index={view.index} />
                <TrackingAddress address={order.shipping_address} />
                <TrackingItems items={order.items} total={order.grand_total} />
              </>
            )}
            {order.delivered && <DeliveredReviewForm items={order.review_items} />}

            <p className="live-track-live">
              <i />
              {order.closed
                ? 'Final status'
                : `Live · updates automatically${
                    checkedAt ? ` · checked ${checkedAt.toLocaleTimeString()}` : ''
                  }`}
            </p>
          </>
        )}

        <Link className="live-track-home" to="/shop">Visit ZEVI</Link>
      </section>
    </main>
  );
}

export default LiveOrderPage;